import {debounce, throttle} from 'throttle-debounce';
import CustomSelect from '../../../form/custom-select';
import Observer from '../../../../common/scripts/observer';
import RangeSlider from '../../../range-slider';
import {TABLET_PORTRAIT_BREAKPOINT} from '../../../../common/scripts/constants';
import {Utils} from '../../../../common/scripts/utils';

const observer = new Observer();

/**
 * 🚩🚩🚩🔴🔴🔴 - RED FLAG 🔴🔴🔴🚩🚩🚩
 * Не делать этот код донором параметрического поиска других проектов без полного рефакторинга
 */

class ParametricFilter {
    constructor(options) {
        /**
         * Параметры
         */
        this.options = options;

        this.wrapper = options.element || null;

        /**
         * Целевой элемент
         */
        this.element = options.element ? options.element.querySelector('.j-parametric-filter') : null;

        /**
         * CSS-класс закрывающегося фильтра
         * @type {string}
         */
        this.openClass = 'is-open';

        /**
         * CSS-класс состояния загрузки
         * @type {string}
         */
        this.loadingClass = 'is-loading';

        /**
         * CSS-класс неактивного состояния
         * @type {string}
         */
        this.disableClass = 'is-disabled';

        /**
         * Класс для элемента фильтра в состоянии загрузки
         * @type {string}
         */
        this.elemLoadingClass = 'is-filter-elem-load';

        /**
         * Группировка по типам
         */
        this.group = 0;

        /**
         * Задержка отложенных функций
         * @type {number}
         */
        this.debTime = 300;

        /**
         * Тип последнего запроса
         * @type {string}
         */
        this.reqType = '';

        /**
         * Флаг, открыт блок фильтров или нет (мобильная версия)
         * @type {boolean}
         */
        this.isOpen = false;

        /**
         * Развёрнуты ли все опции
         * @type {boolean}
         */
        this.isParamsOpen = true;
        this.isMobile = Utils.getWindowWidth() < TABLET_PORTRAIT_BREAKPOINT;
        this.pendingCheckboxes = {};
    }

    init() {
        if (!this.element) {
            return;
        }

        this._initElements();
        this._initSelects();
        this._initSliders();

        this._subscribes();
        this._bindEvents();
    }

    /**
     * Инициализация элементов
     * @private
     */
    _initElements() {
        this.form = this.element.querySelector('.j-parametric-filter__form');
        this.resetBtns = [...this.element.querySelectorAll('.j-parametric-filter__reset')];
        this.submit = this.element.querySelector('.j-parametric-filter__submit');
        this.submitText = this.element.querySelector('.j-parametric-filter__submit-text');
        this.filterElems = Array.from(this.element.querySelectorAll('.j-parametric-filter__elem'));
        this.sameFilters = Array.from(this.wrapper.querySelectorAll('.j-parametric__same-feature'));
        this.groupCheckbox = this.wrapper.querySelector('.j-parametric__group-checkbox');
        this.content = this.element.querySelector('.j-parametric-filter__content');
        this.closeButtons = this.element.querySelectorAll('.j-parametric-filter__close');
        this.toggleBtn = this.element.querySelector('.j-parametric-filter__toggle');
        if (this.toggleBtn) {
            this.toggleBtnCount = this.toggleBtn.querySelector('.j-parametric-filter__toggle-num');
        }
        this.toggleParamsBtn = this.element.querySelector('.j-parametric-filter__toggle-params');
        this.toggleParamsBtnTxt = this.toggleParamsBtn &&
            this.toggleParamsBtn.querySelector('.j-parametric-filter__toggle-params-text');
        this.toggleElement = this.element.querySelector('.j-parametric-filter__elem-toggle');
        this.popupBtn = this.element.querySelector('.j-parametric-filter__popup');
        this.title = this.element.querySelector('.j-parametric-filter__result-title');
        this.hiddenDiscount = this.form.querySelector('.j-hidden-discount-input');
        this.filterWrapper = document.querySelector('.j-wrapper');
    }

    /**
     * Инициализация селектов
     * @private
     */
    _initSelects() {
        this.selectElements = Array.from(this.element.querySelectorAll('.j-parametric-filter__select'));
        this.selects = [];
        this.selectElements.forEach((element) => {
            const select = new CustomSelect({
                elem    : element,
                template: element.dataset.template,
                popup   : false
            });

            select.init();
            this.selects.push(select);
        });
    }

    /**
     * Инициализация рейндж-слайдеров
     * @private
     */
    _initSliders() {
        this.rangeElements = Array.from(this.element.querySelectorAll('.j-range-slider'));
        this.ranges = {};
        this.rangeElements.forEach((element) => {
            const range = new RangeSlider();

            range.init({
                wrapper : element,
                onFinish: debounce(this.debTime, this._send.bind(this, 'filter'))
            });

            this.ranges[element.dataset.slider] = range;
        });
    }

    /**
     * Подписка на события обзёрвера
     * @private
     */
    _subscribes() {
        observer.subscribe('multiselect:change', debounce(this.debTime, this._send.bind(this, 'filter')));

        observer.subscribe('custom-select:close', () => {
            if (this.isMobile && this.isOpen) {
                Utils.bodyFixed(this.content);
            }
        });
    }

    /**
     * Вешает обработчики событий
     * @private
     */
    _bindEvents() {
        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, throttle(50, () => {
                this.isMobile = Utils.getWindowWidth() < TABLET_PORTRAIT_BREAKPOINT;
            }));
        });

        this.form.addEventListener('submit', (event) => {
            event.preventDefault();
        });

        this.submit.addEventListener('click', () => {
            this._send('filter');
        });

        if (!this.isMobile) {
            this.form.addEventListener('change', debounce(this.debTime, this._send.bind(this, 'filter')));
        }

        this.resetBtns.forEach((resetBtn) => {
            resetBtn.addEventListener('click', debounce(this.debTime, this._send.bind(this, 'reset')));
        });

        this.sameFilters.forEach((elem) => {
            elem.addEventListener('change', this._updateSameFilters.bind(this));
        });

        this.filterElems.forEach((elem) => {
            elem.addEventListener('click', () => {
                this.lastFilterElem = elem;
            });
        });

        if (this.toggleBtn) {
            this.toggleBtn.addEventListener('click', this._setOpenState.bind(this));
        }

        if (this.closeButtons) {
            for (const closeBtn of this.closeButtons) {
                closeBtn.addEventListener('click', this._closeFilter.bind(this));
            }
        }

        if (this.toggleParamsBtn) {
            this.toggleParamsBtn.addEventListener('click', this._toggleParams.bind(this));
        }

        if (this.groupCheckbox) {
            this.groupCheckbox.addEventListener('change', (event) => {
                this.group = event.target.checked ? 1 : 0;
                this._updateFilterGroupCheckbox();
            });
        }
    }

    /**
     * Синхронизирует одинаковые фильтры
     * @param {Object} event - событие, после которого надо обновить фильтры
     * @private
     */
    _updateSameFilters(event) {
        const target = event.target;

        if (!target.closest('form')) {
            this.form.dispatchEvent(new Event('change'));
        }
    }

    /**
     * Определяет, сгруппированы результаты или нет
     * @return {number|{}} - состояние группировки
     * @private
     */
    _getGroupingStatus() {
        return this.groupCheckbox && !this.groupCheckbox.checked ? 0 : {};
    }

    /**
     * Задаёт состояние группировки
     * @param {Number} state - сгруппированы результаты или нет
     */
    setResultGroupCheckboxState(state) {
        this.groupCheckbox.checked = state;
    }

    /**
     * Обновляет состояние чекбокса группировки - запускает коллбек из options
     * @private
     */
    _updateFilterGroupCheckbox() {
        if (this.options.updateFilterGroupCheckbox) {
            this.options.updateFilterGroupCheckbox(this.group);
        }
    }

    /**
     * Отправка данных
     * @param {string} reqType - тип запроса
     * @private
     */
    _send(reqType) {
        this.reqType = reqType;

        if (reqType === 'reset') {
            this.lastFilterElem = null;
            observer.publish('parametric:reset');
            this.setDiscountValue(false);
        }

        if (this.options.change) {
            const grouping = this._getGroupingStatus(this.reqType);

            this.options.change(reqType, grouping);
        }
    }

    /**
     * Обновляет фильтры
     * @param {object} filters - данные с сервера
     * @private
     */
    _updateFilters(filters) {
        Object.entries(filters).forEach(([name, {type, data}]) => {
            if (name === 'discount' && data?.length) {
                observer.publish('filters:discounts', data[0].disabled);

                return;
            }

            const filter = this.element.querySelector(`[data-filter="${name}"]`);

            if (!filter) {
                return;
            }

            switch (type) {
                case 'checkbox':
                    this._updateChecks(filter, data);
                    break;
                case 'slider':
                    this._updateRanges(name, data);
                    break;
                case 'select':
                    this._updateSelect(data);
                    break;
                default:
                    break;
            }
        });
    }

    /**
     * Обновление чекбоксов
     * @param {HTMLObjectElement} filter - враппер с фильтрами
     * @param {object} data - данные для обработки
     * @private
     */
    _updateChecks(filter, data = []) {
        data.forEach((option) => {
            const input = filter.querySelector(`input[value="${option.value}"]`);

            if (!input) {
                return;
            }

            input.disabled = option.disabled;
            input.checked = option.checked;
        });
    }

    /**
     * Обновляет рейндж-слайдеры после того, как пришли новые данные с сервера
     * @param {String} name - имя рейндж слайдера
     * @param {Object} data - данные с сервера
     * @private
     */
    _updateRanges(name, data) {
        const range = this.ranges[name];

        if (range) {
            range.update(data);
        }
    }

    /**
     * Обновляет данные селектов
     * @param {Object} data - данные с сервера
     * @private
     */
    _updateSelect(data) {
        const targetSelect = this.selects.find((select) => {
            return select.elem.dataset.name === data.name;
        });

        if (!targetSelect) {
            return;
        }

        targetSelect.update(data.options);
    }

    /**
     * Обновляет кнопку фильтра по данным с сервера
     * @param {string} count - строка с количеством найденных квартир
     * @private
     */
    _updateSubmit(count) {
        if (!this.submit) {
            return;
        }

        this.submit.classList.toggle(this.disableClass, !count);
        this.submit[count ? 'removeAttribute' : 'setAttribute']('disabled', '');
        this._replaceContent(this.submitText, count || 'Вариантов не найдено');
    }

    /**
     * Обновляет число активных фильтров
     * @param {Number} count - число активных фильтров
     * @private
     */
    _updateFiltersCount(count) {
        if (!this.toggleBtnCount) {
            return;
        }

        this.toggleBtnCount.classList[count ? 'remove' : 'add'](this.disableClass);
        this._replaceContent(this.toggleBtnCount, count.toString());
    }

    /**
     * Обновляет данные попапа (расположения паркинга) данными с сервера
     * @param {String} data - данные попапа с сервера
     * @private
     */
    _updatePopupData(data) {
        if (!this.popupBtn) {
            return;
        }

        this.popupBtn.dataset.json = data;
    }

    /**
     * Заменяет контент в блоке на переданный
     * @param {Element} element - целевой блок
     * @param {string} html - контент, который будет подставлен
     * @private
     */
    _replaceContent(element, html) {
        Utils.clearHtml(element);

        element.insertAdjacentHTML('beforeend', html);
    }

    /**
     * Сворачивает/разворачивает фильтр (до 960px)
     * @private
     */
    _setOpenState() {
        this[this.isOpen ? '_closeFilter' : '_openFilter']();
    }

    /**
     * Разворачивает фильтр
     * @private
     */
    _openFilter() {
        this.content.classList.add(this.openClass);
        this.content.style.height = `${this.form.offsetHeight}px`;
        this.filterWrapper.classList.add('is-active');
        setTimeout(() => {
            this.content.style.height = 'auto';
            this.isOpen = true;
        }, 600);
        Utils.bodyFixed(this.content);
    }

    /**
     * Сворачивает фильтр
     * @private
     */
    _closeFilter() {
        this.content.style.height = `${this.form.offsetHeight}px`;
        this.filterWrapper.classList.remove('is-active');
        this.content.classList.remove(this.openClass);
        this.content.style.height = 0;
        this.isOpen = false;
        Utils.bodyStatic();
    }

    /**
     * Сворачивает/разворачивает дополнительные параметры фильтра
     * @private
     */
    _toggleParams() {
        if (this.toggleParamsBtnTxt) {
            this.toggleParamsBtnTxt.innerHTML = this.isParamsOpen ? 'Расширенный фильтр' : 'Простой фильтр';
        }

        if (this.toggleParamsBtn) {
            this.toggleParamsBtn.classList.toggle('is-active');
        }

        this.toggleElement.classList.toggle('is-visible');
        this.isParamsOpen = !this.isParamsOpen;
    }

    /**
     * Применяет данные с сервера
     * @param {object} data - данные с сервера
     * @param {string} reqType - тип произошедшего запроса
     */
    setFilter(data, reqType) {
        this.reqType = reqType;
        this._updateSubmit(data.count);

        if (data.filters) {
            this._updateFilters(data.filters);
        }

        if (data.popup) {
            this._updatePopupData(data.popup);
        }

        if ('filtersCount' in data || this.reqType === 'reset') {
            this._updateFiltersCount(data.filtersCount || 0);
        }
    }

    /**
     * Запускает состояние ожидания
     * @param {boolean} isSubmitLoading - надо ли лоадить кнопку поиска
     */
    runLoading(isSubmitLoading = false) {
        this.element.classList.add(this.loadingClass);

        if (isSubmitLoading) {
            if (this.submit) {
                this.submit.classList.add(this.loadingClass);
            }

            this.filterElems.forEach((elem) => {
                if (this.lastFilterElem !== elem) {
                    elem.classList.add(this.elemLoadingClass);
                }
            });
        }
    }

    /**
     * Отменяет состояние ожидания
     */
    stopLoading() {
        if (!this.element) {
            return;
        }

        this.element.classList.remove(this.loadingClass);
        if (this.submit) {
            this.submit.classList.remove(this.loadingClass);
        }
        this.filterElems.forEach((elem) => {
            elem.classList.remove(this.elemLoadingClass);
        });
    }

    /**
     * Возвращает форму
     * @returns {Element} - элемент формы
     */
    getForm() {
        return this.element ? this.form : null;
    }

    /**
     * Проходит по рейнджам и конвертирует значения инпутов в числа
     * @param {object} formData - формдата, в которой нужно переписать значения
     */
    convertRanges(formData) {
        if (!this.element) {
            return;
        }

        Object.values(this.ranges).forEach((range) => {
            const inputs = range.getInputs();

            inputs.forEach((input, index) => {
                const name = input.name;

                const val = Number(input.value.replace(/\D+/g, '.'));

                const isChanged = index === 0 ? range.changeFrom : range.changeTo;

                if (isChanged) {
                    formData.set(name, val);
                } else {
                    formData.delete(name);
                }
            });
        });
    }

    /**
     * Устанавливает состояние фильтра
     * @param {boolean} disable - флаг состояния дисейбла (неактивен/активен)
     */
    setDisable(disable = false) {
        this.element.classList[disable ? 'add' : 'remove']('is-disabled');
    }

    /**
     * Коллбек для обновления скрытого инпута скидочности
     * (вызывается из модуля результатов)
     * @param {boolean} value - флаг скидочности
     */
    setDiscountValue(value) {
        if (this.hiddenDiscount) {
            this.hiddenDiscount.value = value;
        }
    }
}

export default ParametricFilter;
